import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import EMRDashboard from './EMRDashboard';
import { 
  Users, Search, Heart, Phone, MapPin, Calendar, 
  FileText, Activity, ChevronRight, Loader2, ArrowLeft,
  Stethoscope, ClipboardList, Clock, User
} from 'lucide-react';

export default function MyPatients({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showEMR, setShowEMR] = useState(false);
  const [patientDetails, setPatientDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchMyPatients();
  }, []);

  const fetchMyPatients = async () => {
    try {
      const response = await api.get('/caregiver/my-patients');
      setPatients(response.data.patients || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error('Failed to load your patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientDetails = async (patientId) => {
    setLoadingDetails(true);
    try {
      const response = await api.get(`/caregiver/patient/${patientId}/details`);
      setPatientDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch patient details:', error);
      toast.error('Failed to load patient details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    fetchPatientDetails(patient.id);
  };

  const handleOpenEMR = () => {
    setShowEMR(true);
  };

  const handleBackFromEMR = () => {
    setShowEMR(false);
  };

  const handleBackToList = () => {
    setSelectedPatient(null);
    setPatientDetails(null);
  };

  const filteredPatients = patients.filter(patient => 
    patient.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    patient.diagnosis?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Full Screen EMR View
  if (showEMR && selectedPatient) {
    return (
      <div className="min-h-screen bg-gradient-subtle">
        {/* EMR Header */}
        <div className="nav-premium sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleBackFromEMR}
                  variant="ghost"
                  className="text-purple-600 hover:bg-purple-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Patient
                </Button>
                <div className="border-l border-purple-200 pl-4">
                  <h1 className="text-lg font-bold text-purple-900">
                    {selectedPatient.full_name} - EMR
                  </h1>
                  <p className="text-xs text-gray-500">Electronic Medical Record</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Screen EMR Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <EMRDashboard 
            clientId={selectedPatient.id}
            shiftId={null}
          />
        </div>
      </div>
    );
  }

  // Patient Detail View
  if (selectedPatient) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <Button
          onClick={handleBackToList}
          variant="ghost"
          className="text-purple-600 hover:bg-purple-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to My Patients
        </Button>

        {/* Patient Header */}
        <div className="card-premium p-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{selectedPatient.full_name}</h2>
                <p className="text-purple-200">
                  {selectedPatient.diagnosis || 'No diagnosis on file'}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm text-purple-100">
                  {selectedPatient.date_of_birth && (
                    <span>DOB: {formatDate(selectedPatient.date_of_birth)}</span>
                  )}
                  {selectedPatient.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {selectedPatient.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              onClick={handleOpenEMR}
              className="bg-white text-purple-700 hover:bg-purple-50"
              data-testid="open-full-emr-btn"
            >
              <Stethoscope className="w-4 h-4 mr-2" />
              Open Full EMR
            </Button>
          </div>
        </div>

        {loadingDetails ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : patientDetails ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact & Demographics */}
            <div className="card-premium p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                Demographics
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Full Name</span>
                  <span className="font-medium text-purple-900">{patientDetails.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date of Birth</span>
                  <span className="font-medium text-purple-900">{formatDate(patientDetails.date_of_birth)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone</span>
                  <span className="font-medium text-purple-900">{patientDetails.phone || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Address</span>
                  <span className="font-medium text-purple-900 text-right max-w-[200px]">
                    {patientDetails.address || 'N/A'}
                  </span>
                </div>
                {patientDetails.emergency_contact && (
                  <>
                    <div className="border-t border-purple-100 pt-3 mt-3">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Emergency Contact</p>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name</span>
                      <span className="font-medium text-purple-900">{patientDetails.emergency_contact.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Phone</span>
                      <span className="font-medium text-purple-900">{patientDetails.emergency_contact.phone}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Care Plan Summary */}
            <div className="card-premium p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-purple-600" />
                Care Plan Summary
              </h3>
              {patientDetails.care_plan ? (
                <div className="space-y-3">
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1">Primary Goals</p>
                    <p className="text-sm text-purple-900">{patientDetails.care_plan.goals || 'No goals specified'}</p>
                  </div>
                  {patientDetails.care_plan.interventions?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Key Interventions</p>
                      <ul className="space-y-2">
                        {patientDetails.care_plan.interventions.slice(0, 4).map((int, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                            {int.intervention}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No care plan on file</p>
              )}
            </div>

            {/* Recent Visits */}
            <div className="card-premium p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Recent Visits
              </h3>
              {patientDetails.recent_visits?.length > 0 ? (
                <div className="space-y-3">
                  {patientDetails.recent_visits.map((visit, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-purple-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-purple-900">{formatDate(visit.date)}</p>
                        <p className="text-xs text-gray-500">{visit.duration} hours</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        visit.status === 'completed' ? 'bg-green-100 text-green-700' :
                        visit.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {visit.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No recent visits</p>
              )}
            </div>

            {/* Vital Signs Summary */}
            <div className="card-premium p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Latest Vitals
              </h3>
              {patientDetails.latest_vitals ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Blood Pressure</p>
                    <p className="text-lg font-bold text-purple-900">
                      {patientDetails.latest_vitals.blood_pressure || '--/--'}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Heart Rate</p>
                    <p className="text-lg font-bold text-purple-900">
                      {patientDetails.latest_vitals.heart_rate || '--'} bpm
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Temperature</p>
                    <p className="text-lg font-bold text-purple-900">
                      {patientDetails.latest_vitals.temperature || '--'}°F
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">O2 Saturation</p>
                    <p className="text-lg font-bold text-purple-900">
                      {patientDetails.latest_vitals.oxygen_saturation || '--'}%
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No vitals recorded</p>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // Patient List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="section-header">
        <h2 className="section-title">My Patients</h2>
        <p className="section-subtitle">View and manage your assigned patients</p>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search patients by name or diagnosis..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="patient-search-input"
          />
        </div>
        <div className="text-sm text-gray-500">
          {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''} assigned
        </div>
      </div>

      {/* Patient List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : filteredPatients.length === 0 ? (
        <div className="card-premium p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-purple-900 mb-2">No Patients Assigned</h3>
          <p className="text-gray-500">
            {searchQuery ? 'No patients match your search' : 'You have no patients assigned to you yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredPatients.map((patient) => (
            <div
              key={patient.id}
              onClick={() => handleSelectPatient(patient)}
              className="card-premium p-5 cursor-pointer group"
              data-testid={`patient-card-${patient.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-900 group-hover:text-purple-700">
                      {patient.full_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {patient.diagnosis || 'No diagnosis'}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      {patient.address && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {patient.address.split(',')[0]}
                        </span>
                      )}
                      {patient.last_visit && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last: {formatDate(patient.last_visit)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
